import React from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import cx from 'clsx'
import LayoutContainer from 'components/Common/LayoutContainer'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { NavWrapper } from 'components/OfferPage/styles'
import { OFFER_TYPE_HOTEL_SLUG } from 'constants/offer'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import {
  arePricesLoading,
  getFromDurationLabel,
  isAvailableForPurchase,
} from 'selectors/offerPage/offerPageSelectors'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import WalledContent from 'components/Common/WalledContent'
import TextButton from 'components/Luxkit/Button/TextButton'

import BodyText from 'components/Luxkit/Typography/BodyText'
import DesktopRelatedTravelItems from 'components/OfferPage/RelatedTravelItems'
import config from 'constants/config'
import zIndex from 'styles/tools/z-index'
import GenericLoginSignupWall from 'components/Common/WalledContent/GenericLoginSignupWall'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { useBusinessTravellerStickyBudgetBarHeightContext } from 'businessTraveller/utils/useBusinessBudgetBarHeightContext'
import PriceRowPrice from 'components/Luxkit/PricePoints/PriceRowPrice'
import PriceRowPriceCaption from 'components/Luxkit/PricePoints/PriceRowPriceCaption'
import Group from 'components/utils/Group'
import PriceRowLuxPlusPrice from 'components/Luxkit/PricePoints/LuxPlus/PriceRowLuxPlusPrice'
import LuxPlusPriceGuard from 'luxPlus/components/LuxPlusPriceGuard'
import useCheapestPropertyPricing from 'hooks/OfferPage/useCheapestPropertyPricing'

const Sticky = styled(NavWrapper)`
  top: 0;
  position: sticky;
  z-index: ${zIndex.stickyHeader};

  &.hideOnMobile.hideOnMobile {
    display: none;
    ${mediaQueryUp.tablet} {
      display: flex;
      visibility: visible;
    }
  }
`

const Root = styled(LayoutContainer)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex: 1;

  ${mediaQueryUp.tablet} {
    margin-left: auto;
    justify-content: flex-end;
  }

  > * + * {
    margin-left: ${rem(12)};
  }
`

const MobileNewLine = styled.br`
  ${mediaQueryUp.tablet} {
    display: none;
  }
`

const PriceWrapper = styled.div`
  display: flex;
  margin-left: auto;
  justify-content: flex-end;
  position: relative;

  & > * + * {
    margin-left: ${rem(8)};
  }
`
const InlineWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;

  & > * + * {
    margin-left: ${rem(8)};
  }

  ${mediaQueryUp.tablet} {
    justify-content: flex-end;
  }
`

interface Props {
  offer: App.HotelOffer;
  onViewPackageOptions: () => void;
  calendarsLoading?: boolean;
  headlessMode: boolean;
  durationLabel: string;
  availableForPurchase?: boolean;
}

function AlwaysOnStickyHeader(props: Props) {
  const {
    offer,
    onViewPackageOptions,
    calendarsLoading,
    headlessMode,
    durationLabel,
    availableForPurchase,
  } = props

  const [hotelPricing, memberPricing] = useCheapestPropertyPricing(offer.property.id)

  const dispatch = useOfferPageDispatch()

  const onViewOptionsClick = () => {
    dispatch({ type: OfferPageActions.closeFilter })
    onViewPackageOptions()
  }
  const displaySoldOutLabel = !availableForPurchase && !config.LERE_EXPIRED_DEALS_V2_ENABLED
  const hideSoldOutOnMobile = !availableForPurchase && config.LERE_EXPIRED_DEALS_V2_ENABLED

  const businessTravellerStickyBudgetBarStyle = useBusinessTravellerStickyBudgetBarHeightContext()

  return (
    <Sticky
      style={businessTravellerStickyBudgetBarStyle}
      shadow={!hideSoldOutOnMobile ? 'bottom-small' : undefined}
      className={cx({ hideOnMobile: hideSoldOutOnMobile })}
    >
      <Root>
        {!headlessMode && <DesktopRelatedTravelItems offer={offer} />}
        {availableForPurchase && (
          <WalledContent enforced={offer.walledGarden} wall={<GenericLoginSignupWall />}>
            <PriceWrapper>
              <Group direction="vertical" horizontalAlign="end">
                <PriceRowPriceCaption>{durationLabel}</PriceRowPriceCaption>
                <LuxPlusPriceGuard
                  showMemberPrice={!!memberPricing?.price}
                  fallbackPrice={<PriceRowPrice
                    size="S"
                    price={hotelPricing.price}
                    saleUnit={hotelPricing.saleUnit}
                  />}
                >
                  <PriceRowLuxPlusPrice
                    size="M"
                    memberPrice={memberPricing?.price ?? 0}
                    saleUnit={memberPricing?.saleUnit ?? ''}
                    logoPlacement="leading"
                  />
                </LuxPlusPriceGuard>
                <LoadingIndicator visible={calendarsLoading} floating opaque />
              </Group>
              <TextButton kind="primary" data-testid="offer-view-options" onClick={onViewOptionsClick}>
                <CSSBreakpoint max="tablet" as="span">
                  View options
                </CSSBreakpoint>
                <CSSBreakpoint min="desktop" as="span">
                  View package options
                </CSSBreakpoint>
              </TextButton>
            </PriceWrapper>
          </WalledContent>
        )}
        {!availableForPurchase && displaySoldOutLabel &&
          <InlineWrapper>
            <BodyText variant="medium" weight="bold" colour="neutral-one">
              This offer has<MobileNewLine /> SOLD OUT
            </BodyText>
            <TextButton kind="primary" to={`/${OFFER_TYPE_HOTEL_SLUG}`}>
              Browse all hotel offers
            </TextButton>
          </InlineWrapper>
        }
      </Root>
    </Sticky>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  calendarsLoading: arePricesLoading(appState, state),
  headlessMode: appState.system.headlessMode,
  durationLabel: getFromDurationLabel(state, appState),
  availableForPurchase: isAvailableForPurchase(state, appState),
}))(AlwaysOnStickyHeader)
